import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A manually scrolled row of category cards.
 *
 * Measures the row against its viewport and reflects the result on the host element:
 * `overflowing` when the cards don't all fit (the section uses it to reveal the arrows
 * and the edge shades), plus `at-start` / `at-end` so each shade and arrow can hide
 * itself once there is nothing left to scroll towards on that side.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller - The horizontally scrollable viewport.
 * @property {HTMLElement} items - The row of category cards inside the viewport.
 * @property {HTMLButtonElement} [previous] - The scroll-towards-start control.
 * @property {HTMLButtonElement} [next] - The scroll-towards-end control.
 *
 * @extends Component<Refs>
 */
class CategoryCarouselComponent extends Component {
  requiredRefs = ['scroller', 'items'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#resizeObserver.observe(this.refs.scroller);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.scroller?.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();

    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  /** Scrolls one page towards the start. Bound declaratively via `on:click`. */
  previous() {
    this.#scrollByPage(-1);
  }

  /** Scrolls one page towards the end. Bound declaratively via `on:click`. */
  next() {
    this.#scrollByPage(1);
  }

  /**
   * @type {number}
   */
  #frame = 0;

  /**
   * Scrolls by as many whole cards as currently fit in the viewport, so a click never
   * leaves a card half out of view.
   *
   * @param {number} direction - `-1` towards the start, `1` towards the end.
   */
  #scrollByPage(direction) {
    const { scroller } = this.refs;

    // In RTL the start edge is on the right and scrollLeft runs negative.
    const sign = getComputedStyle(this).direction === 'rtl' ? -1 : 1;

    scroller.scrollBy({ left: this.#pageDistance() * direction * sign });
  }

  #pageDistance() {
    const { scroller, items } = this.refs;
    const card = items.firstElementChild;

    if (!card) return scroller.clientWidth;

    const gap = parseFloat(getComputedStyle(items).columnGap) || 0;
    const step = card.getBoundingClientRect().width + gap;

    if (!step) return scroller.clientWidth;

    const cardsPerPage = Math.max(1, Math.floor((scroller.clientWidth + gap) / step));

    return step * cardsPerPage;
  }

  #update = () => {
    const { scroller, previous, next } = this.refs;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    // Sub-pixel layout rounding leaves a fraction of a pixel of scroll on rows that
    // visually fit, so treat anything under a pixel as "no overflow".
    const overflowing = maxScroll > 1;
    const position = Math.abs(scroller.scrollLeft);
    const atStart = !overflowing || position <= 1;
    const atEnd = !overflowing || position >= maxScroll - 1;

    this.toggleAttribute('overflowing', overflowing);
    this.toggleAttribute('at-start', atStart);
    this.toggleAttribute('at-end', atEnd);

    if (previous) previous.disabled = atStart;
    if (next) next.disabled = atEnd;
  };

  #handleScroll = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #resizeObserver = new ResizeObserver(debounce(() => this.#update(), 100));
}

if (!customElements.get('category-carousel-component')) {
  customElements.define('category-carousel-component', CategoryCarouselComponent);
}
